import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/api/client';
import { Save, User, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { PushToggleButton } from '@/components/PwaPrompts';

const SEX = ['Male','Female','Prefer Not To Say'];
const MARITAL = ['Single','Married','Widowed','Separated','Prefer Not To Say'];

export default function Profile() {
  const qc = useQueryClient();
  const { data: profile } = useQuery({ queryKey: ['member-profile'], queryFn: async () => (await api.get('/member/profile')).data });
  const [form, setForm] = useState(null);

  useEffect(() => { if (profile) setForm(profile); }, [profile]);

  const mut = useMutation({
    mutationFn: async (payload) => (await api.put('/member/profile', payload)).data,
    onSuccess: () => { toast.success('Profile saved'); qc.invalidateQueries({ queryKey: ['member-profile'] }); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Could not save'),
  });

  if (!form) return <div className="text-moj-purple">Loading…</div>;

  const onSubmit = (e) => {
    e.preventDefault();
    const { google_email, picture, role, status, id, auth_user_id, password_hash, created_at, updated_at, ...payload } = form;
    mut.mutate(payload);
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6" data-testid="profile-form">
      <div className="card-moj p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-6">
          <User size={28} className="text-moj-purple"/>
          <h1 className="font-serif text-2xl text-moj-purple-deep">My Profile</h1>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="label-moj">Full Name</label><input className="input-moj" value={form.full_name || ''} onChange={(e) => setForm({...form, full_name: e.target.value})}/></div>
          <div><label className="label-moj">Email (Google — read only)</label><input className="input-moj bg-gray-50" value={form.google_email || ''} readOnly/></div>
          <div><label className="label-moj">First Name</label><input className="input-moj" value={form.first_name || ''} onChange={(e) => setForm({...form, first_name: e.target.value})}/></div>
          <div><label className="label-moj">Last Name</label><input className="input-moj" value={form.last_name || ''} onChange={(e) => setForm({...form, last_name: e.target.value})}/></div>
          <div className="sm:col-span-2"><label className="label-moj">Address in Ireland</label><input className="input-moj" value={form.ireland_address || ''} onChange={(e) => setForm({...form, ireland_address: e.target.value})}/></div>
          <div><label className="label-moj">Eircode (auto-uppercase)</label><input className="input-moj uppercase" value={form.eircode || ''} onChange={(e) => setForm({...form, eircode: e.target.value.toUpperCase()})} placeholder="e.g. D10 NH74"/></div>
          <div><label className="label-moj">Phone</label><input className="input-moj" value={form.phone || ''} onChange={(e) => setForm({...form, phone: e.target.value})}/></div>
          <div className="sm:col-span-2"><label className="label-moj">Address in Home Country</label><input className="input-moj" value={form.home_country_address || ''} onChange={(e) => setForm({...form, home_country_address: e.target.value})}/></div>
          <div><label className="label-moj">Date of Birth</label><input type="date" className="input-moj" value={form.dob || ''} onChange={(e) => setForm({...form, dob: e.target.value})}/></div>
          <div><label className="label-moj">Sex</label>
            <select className="input-moj" value={form.sex || ''} onChange={(e) => setForm({...form, sex: e.target.value})}>
              <option value="">Select…</option>
              {SEX.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div><label className="label-moj">Marital Status</label>
            <select className="input-moj" value={form.marital_status || ''} onChange={(e) => setForm({...form, marital_status: e.target.value})}>
              <option value="">Select…</option>
              {MARITAL.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {form.marital_status === 'Married' && (
            <div><label className="label-moj">Wedding Anniversary</label><input type="date" className="input-moj" value={form.wedding_anniversary_date || ''} onChange={(e) => setForm({...form, wedding_anniversary_date: e.target.value})}/></div>
          )}
        </div>
      </div>

      <div className="card-moj p-5 flex items-center justify-between">
        <p className="text-sm text-moj-charcoal/70">Your privacy is sacred. Profile data is used only for church communication.</p>
        <button type="submit" disabled={mut.isPending} className="btn-primary" data-testid="profile-save"><Save size={16}/> {mut.isPending ? 'Saving…' : 'Save Profile'}</button>
      </div>

      {/* Notification preferences — browser Push subscription managed via SW.
          Shown here so members can toggle without hunting through settings. */}
      <div className="card-moj p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:justify-between" data-testid="profile-notifications">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-moj-purple-deep text-moj-gold flex items-center justify-center flex-shrink-0">
            <Bell size={18} />
          </div>
          <div className="min-w-0">
            <h2 className="font-serif text-lg text-moj-purple-deep leading-tight">Notifications</h2>
            <p className="text-sm text-moj-charcoal/70 mt-0.5">
              Get a ping when the live service starts, new leaflets drop, or there's a prayer update. You can turn this off anytime.
            </p>
          </div>
        </div>
        <PushToggleButton userId={form?.id || null} />
      </div>
    </form>
  );
}
